import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Heart, ShoppingCart, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '../lib/imageUrl';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
    checkFavorite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lang]);

  const loadProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      toast.error(t('productNotFound'));
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await api.get('/favorites');
      setIsFavorite(response.data.some((p) => p.id === id));
    } catch (error) {
      console.error('Failed to check favorite', error);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success(t('removedFromFavorites'));
      } else {
        await api.post(`/favorites/${id}`);
        setIsFavorite(true);
        toast.success(t('addedToFavorites'));
      }
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(t('addedToCart'));
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-12 text-center">{t('loading')}</div>;
  }

  if (!product) return null;

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <div className="aspect-square bg-white rounded-md overflow-hidden flex items-center justify-center p-4">
          <img
            src={resolveImageUrl(product.image_url)}
            alt={product.name}
            className="w-full h-full object-contain"
            data-testid="product-image"
          />
        </div>

        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4" data-testid="product-name">
            {product.name}
          </h1>
          <p className="text-4xl font-bold mb-6" data-testid="product-price">
            €{product.price.toFixed(2)}
          </p>
          <p className="text-lg text-muted-foreground mb-8" data-testid="product-description">
            {product.description}
          </p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-border rounded-md">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-3 hover:bg-muted"
                data-testid="decrease-quantity"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-6 text-lg font-semibold" data-testid="quantity-display">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-3 hover:bg-muted"
                data-testid="increase-quantity"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex gap-4">
            <Button onClick={handleAddToCart} className="flex-1 h-14 text-lg" data-testid="add-to-cart-button">
              <ShoppingCart className="h-5 w-5 me-2" />
              {t('addToCart')}
            </Button>
            <Button
              variant={isFavorite ? 'default' : 'outline'}
              size="icon"
              onClick={toggleFavorite}
              className="h-14 w-14"
              data-testid="favorite-button"
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;